import React, {useEffect, useState, useRef, MutableRefObject} from "react";
import "./DetailsItem.css";
import Collapsible from "../../shared/collapsible/collapsible";
import TransloadTerminal from "../../../models/TransloadTerminal";
const thumbs = require.context('../../../img/thumbnails', false);

interface iProps {
  transloadTerminal: TransloadTerminal;
  expanded: boolean;
  zoomToHandler: Function;
  expandHandler?: Function;
  onMouseEnter?: Function;
  onMouseLeave?: Function;
  onContactUsClick: Function;
  transloadLayer?: MutableRefObject<__esri.FeatureLayer | undefined>;
};

export default function DetailsItem(props: iProps) {
  const hasFetchedTerminals = useRef(false);
  const [loading, setLoading] = useState(false);
  const [terminals, setTerminals] = useState<any[]>([]);

  const defaultThumbKey = './WATCO.svg';
  const thumbKey = thumbs.keys().find((key: string) => key.startsWith(`./${props.transloadTerminal.publicName.replaceAll(" ","_")}`));
  let thumb = '';
  if (thumbKey) {
    thumb = thumbs(thumbKey);
  } else {
    thumb = thumbs(defaultThumbKey);
  }

  useEffect(() => {
    if (props.expanded && props.transloadLayer?.current && !hasFetchedTerminals.current) {
      hasFetchedTerminals.current = true;
      setLoading(true);
      props.transloadLayer.current.queryFeatures({
        where: `Terminal_Name = '${props.transloadTerminal.publicName}'`,
        returnGeometry: false,
        outFields: ["*"],
        returnDistinctValues: true
      }).then(featureSet => {
        const terminalsDict: {[key: string]: any} = {};
        featureSet.features.forEach(feature => {
          const atts = feature.attributes;
          const key = atts.Street_Address;
          if (atts.via) {
            atts.Class_1_Code += ` (via ${atts.via})`;
          }
          if (terminalsDict[key]) {
            terminalsDict[key].Class_1_Code += `,${atts.Class_1_Code}`;
          } else {
            terminalsDict[key] = atts;
          }
        })
        setTerminals(Object.values(terminalsDict));
        setLoading(false);
      })
    }
  }, [props.expanded, props.transloadLayer, props.transloadTerminal])


  const onItemClick = (evt: any) => {
    if (props.expandHandler) {
      props.expandHandler();
    }
  }

  const onContactUsClick = (evt: any) => {
    props.onContactUsClick();
    evt.stopPropagation();
  }

  const onZoomToClick = (evt: any) => {
    props.zoomToHandler();
    evt.stopPropagation();
  }

  const onMouseEnter = (evt: any) => {
    if (props.onMouseEnter) {
      props.onMouseEnter();
    }
  }

  const onMouseLeave = (evt: any) => {
    if (props.onMouseLeave) {
      props.onMouseLeave();
    }
  }

  return (
    <div className={`flex-column transloading-details-item ${props.expanded ? 'expanded' : 'reduced'}`}
      onClick={onItemClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <div className="flex-row">
        <img className='thumbnail' src={thumb} alt={props.transloadTerminal.publicName} />
        <div className='flex-column details'>
          <div className='public-name'>{props.transloadTerminal.publicName}</div>
          <div className="btn-container">
            <button className="link-btn small-font" onClick={onContactUsClick}>Contact Us</button>
            <button className="link-btn small-font" onClick={onZoomToClick}>Zoom To</button>
            {props.transloadTerminal.url &&
              <a onClick={(evt: any) => {evt.stopPropagation()}}
                target="_blank"
                rel="noreferrer"
                href={props.transloadTerminal.url}>
                Website
              </a>
            }
          </div>
        </div>
      </div>

      {props.expanded &&
        <div className="flex-column expanded-details">
          {loading &&
            <div>
              Loading transload terminals...
            </div>
          }
          {!loading && terminals.length === 0 &&
            <div>
              No associated transload terminals.
            </div>
          }
          {!loading && terminals.length > 0 &&
            <>
              {/* <div className="interchange-title bold">Transload Terminals</div> */}
              {terminals.map((terminal, index) =>
                <Collapsible className="interchange-collapsible"
                  initiallyOpen={index === 0}
                  key={index}
                  buttonLabel={`${terminal.Street_Address}, ${terminal.City}`}
                  buttonClass="interchange-collapsible-btn">
                  <div key={index} className="flex-col interchange-table">
                    <div className="flex-row interchange-table-row">
                      <div>Class I Railroad</div>
                      <div>
                        {terminal.Class_1_Code?.split(',').sort().map((code: string, i: number) => (
                          <div key={i}>{code}</div>))
                        }
                      </div>
                    </div>

                    <div className="flex-row interchange-table-row">
                      <div>Project Cargo</div>
                      <div>{terminal.Project_Cargo === "P" ? "Yes" : "No"}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Liquids</div>
                      <div>{terminal.Liquids === "P" ? "Yes" : "No"}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Bulk</div>
                      <div>{terminal.Bulk_ === "P" ? "Yes" : "No"}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Breakbulk</div>
                      <div>{terminal.Break_Bulk === "P" ? "Yes" : "No"}</div>
                    </div>

                    <div className="flex-row interchange-table-row">
                      <div>Inbound Truck</div>
                      <div>{terminal.IB_Truck === "P" ? "Yes" : "No"}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Inbound Rail</div>
                      <div>{terminal.IB_Rail === "P" ? "Yes" : "No"}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Inbound Barge</div>
                      <div>{terminal.IB_Barge === "P" ? "Yes" : "No"}</div>
                    </div>

                    <div className="flex-row interchange-table-row">
                      <div>Outbound Truck</div>
                      <div>{terminal.OB_Truck === "P" ? "Yes" : "No"}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Outbound Rail</div>
                      <div>{terminal.OB_Rail === "P" ? "Yes" : "No"}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Outbound Barge</div>
                      <div>{terminal.OB_Barge === "P" ? "Yes" : "No"}</div>
                    </div>

                    <div className="flex-row interchange-table-row">
                      <div>Indoor Storage</div>
                      <div>{terminal.Indoor_Storage === "P" ? "Yes" : "No"}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Outdoor Storage</div>
                      <div>{terminal.Outdoor_Storage === "P" ? "Yes" : "No"}</div>
                    </div>

                    <div className="flex-row interchange-table-row">
                      <div>Secure Facility</div>
                      <div>{terminal.secure_facility === "P" ? "Yes" : "No"}</div>
                    </div>
                  </div>
                </Collapsible>
              )}
            </>
          }
        </div>
      }
    </div>
  )
}
